import React, { useState, useRef, useEffect, useMemo } from "react";
import "./HourlyGraph.css";
import { apiCall, getCurrentUser } from "../../utils/api";

const HOUR_ACCENT_CLASSES = ["h1", "h2", "h3", "h4", "h5", "h6", "h7", "h8", "h9", "h10", "h11", "h12"];
const hourAccent = (i) => HOUR_ACCENT_CLASSES[i % HOUR_ACCENT_CLASSES.length];

const ordinal = (n) => {
    const j = n % 10, k = n % 100;
    if (j === 1 && k !== 11) return `${n}st`;
    if (j === 2 && k !== 12) return `${n}nd`;
    if (j === 3 && k !== 13) return `${n}rd`;
    return `${n}th`;
};

const playBeep = () => {
    try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const playTone = (time, duration) => {
            const osc = audioCtx.createOscillator();
            const gain = audioCtx.createGain();
            osc.connect(gain);
            gain.connect(audioCtx.destination);
            osc.type = 'sine';
            osc.frequency.setValueAtTime(880, time); // A5 note
            gain.gain.setValueAtTime(0.08, time);
            gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);
            osc.start(time);
            osc.stop(time + duration);
        };
        playTone(audioCtx.currentTime, 0.25);
        playTone(audioCtx.currentTime + 0.3, 0.25);
    } catch (err) {
        console.warn('Could not play reminder beep:', err);
    }
};

const ROW_BANDS = ["band-a", "band-b", "band-c", "band-d", "band-e", "band-f"];

const vk = (groupKey, colId) => `${groupKey}::${colId}`;

// ── Inline-editable header label (only editable by Admin) ────────────────────
function EditableHeader({ value, onChange, className = "", placeholder = "Label", disabled = false }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    const inputRef = useRef(null);

    useEffect(() => {
        if (editing) setDraft(value);
    }, [editing, value]);

    useEffect(() => {
        if (editing) inputRef.current?.focus();
    }, [editing]);

    const commit = () => {
        const v = draft.trim();
        onChange(v || value);
        setEditing(false);
    };

    if (disabled) {
        return <span className={`${className}`}>{value}</span>;
    }

    if (editing) {
        return (
            <input
                ref={inputRef}
                className={`hg-edit-input ${className}`}
                value={draft}
                placeholder={placeholder}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                    if (e.key === "Enter") commit();
                    if (e.key === "Escape") setEditing(false);
                }}
            />
        );
    }

    return (
        <span
            className={`hg-editable-label ${className}`}
            onClick={(e) => {
                e.stopPropagation();
                setEditing(true);
            }}
            title="Click to rename"
        >
            {value}
        </span>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
// ── Local date helper: returns YYYY-MM-DD in the browser's local timezone ────
// NOTE: do NOT use new Date().toISOString().slice(0, 10) — that gives UTC date
// which can be "tomorrow" for IST users after 18:30 UTC (6:30 PM IST), causing
// the backend's LocalDate.now() comparison to fail and silently reject saves.
const getLocalDate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const parseLocalDate = (dateStr) => {
    if (!dateStr) return new Date();
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
};

export default function HourlyGraph() {
    const currentUser = getCurrentUser();
    const isAdmin = currentUser?.roles?.includes("Admin");
    const isPrivileged = currentUser?.roles?.some(r => r === "Admin" || r === "Manager" || r === "Team Leader");
    const isEditPrivileged = currentUser?.roles?.some(r => r === "Admin" || r === "Manager");
    const currentUserId = currentUser?.userId;
    const showTargetGraph = true;

    const [periodDate, setPeriodDate] = useState(getLocalDate);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [activeDay, setActiveDay] = useState("");
    const [hourCount, setHourCount] = useState(12);
    const [rows, setRows] = useState([]);
    const [openProjectDropdownRowId, setOpenProjectDropdownRowId] = useState(null);

    // Master data from DB
    const [projects, setProjects] = useState([]);
    const [processes, setProcesses] = useState([]);
    const [shifts, setShifts] = useState([]);

    // Target table column groups and values
    const [columnGroups, setColumnGroups] = useState([]);
    const [targetRows, setTargetRows] = useState([]);
    const [addingGroup, setAddingGroup] = useState(null);
    const [newColName, setNewColName] = useState("");

    // Drag-to-reorder state for target rows
    const [dragRowId, setDragRowId] = useState(null);
    const [dragOverRowId, setDragOverRowId] = useState(null);

    const [search, setSearch] = useState("");
    const [savedAt, setSavedAt] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [changeCount, setChangeCount] = useState(0);
    const [autoSaving, setAutoSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [isCheckedIn, setIsCheckedIn] = useState(true); // Default to true to prevent screen flash
    const [isTaskStarted, setIsTaskStarted] = useState(true); // Default to true to prevent screen flash
    const [notification, setNotification] = useState(null); // Floating pop-up notification
    const lastNotifiedHourRef = useRef(null);
    const lastNotifiedDateRef = useRef(null);

    // Keep a live ref to rows so the auto-save closure never goes stale
    const rowsRef = useRef(rows);
    useEffect(() => { rowsRef.current = rows; }, [rows]);

    // Keep a live ref to dirty to avoid overwriting edits during polling
    const dirtyRef = useRef(dirty);
    useEffect(() => { dirtyRef.current = dirty; }, [dirty]);

    // Keep a live ref to periodDate for the auto-save closure
    const periodDateRef = useRef(periodDate);
    useEffect(() => { periodDateRef.current = periodDate; }, [periodDate]);

    // Request desktop notification permission if supported and not asked yet
    useEffect(() => {
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }, []);

    const dateInputRef = useRef(null);
    const monthYearLabel = parseLocalDate(periodDate).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
    const hourLabels = Array.from({ length: hourCount }, (_, i) => ordinal(i + 1));

    const markDirty = () => {
        setDirty(true);
        setChangeCount(c => c + 1);
    };

    const handleDateChange = (newDate) => {
        if (!newDate || newDate === periodDate) return;
        if (dirty) {
            const confirmLeave = window.confirm("You have unsaved changes. Are you sure you want to change the date? Unsaved changes will be lost.");
            if (!confirmLeave) return;
        }
        setDirty(false);
        setError("");
        setPeriodDate(newDate);
    };

    // ── Fetch Setup and Daily Data ──
    useEffect(() => {
        const loadMasterData = async () => {
            try {
                // Verify check-in status for employees
                if (!isAdmin) {
                    const attToday = await apiCall("/attendance/today");
                    setIsCheckedIn(!!attToday?.checkInTime);
                }

                const pData = await apiCall("/projects");
                const procData = await apiCall("/processes");
                const sData = await apiCall("/shifts");
                const settings = await apiCall("/hourly-graph/settings");

                setProjects(pData || []);
                setProcesses(procData || []);
                setShifts(sData || []);
                setColumnGroups(settings?.columnGroups || []);

                // Map projects and default targets
                // Iterate savedTargets FIRST to preserve the user's saved drag order,
                // then append any new projects that have no saved row yet.
                const activeProjects = pData || [];
                const savedTargets = settings?.targetRows || [];
                const mergedTargets = [];

                savedTargets.forEach(r => {
                    const matchingProject = activeProjects.find(p => {
                        const formatted = p.clientName ? `${p.clientName}_${p.name}` : p.name;
                        return formatted === r.project;
                    });
                    mergedTargets.push({
                        ...r,
                        id: r.id || (matchingProject ? `tgt-${matchingProject.id}` : `tgt-custom-${Date.now()}`)
                    });
                });

                // Append any active projects that have no saved target row yet
                activeProjects.forEach(p => {
                    const formatted = p.clientName ? `${p.clientName}_${p.name}` : p.name;
                    if (!mergedTargets.some(mt => mt.project === formatted)) {
                        mergedTargets.push({
                            id: `tgt-${p.id}`,
                            project: formatted,
                            values: {}
                        });
                    }
                });

                setTargetRows(mergedTargets);

            } catch (err) {
                console.error("Failed to fetch setup details:", err);
                setError("Failed to load setup resources.");
            }
        };
        loadMasterData();
    }, [isAdmin]);

    useEffect(() => {
        const loadDailyLogs = async (showLoading = true) => {
            try {
                if (showLoading) setLoading(true);
                const data = await apiCall(`/hourly-graph/logs?date=${periodDate}`);

                // Only update rows if the user has no unsaved changes (to prevent overwriting user input)
                if (!dirtyRef.current) {
                    setRows(data?.rows || []);
                }
                setActiveDay(data?.activeDay || "");

                // Keep check-in status updated dynamically for the current date only
                if (!isAdmin && data?.rows && periodDate === getLocalDate()) {
                    const myRow = data.rows.find(r => r.userId === currentUserId);
                    setIsCheckedIn(!!(myRow?.inTime));
                }

                if (data && data.hasStartedTask !== undefined) {
                    setIsTaskStarted(data.hasStartedTask);
                } else {
                    setIsTaskStarted(true);
                }

                setError("");
            } catch (err) {
                console.error("Failed to load logs:", err);
                setError(err.message || "Failed to fetch hourly production records.");
                if (err.message && err.message.toLowerCase().includes("start a task")) {
                    setIsTaskStarted(false);
                }
            } finally {
                if (showLoading) setLoading(false);
            }
        };

        loadDailyLogs(true);

        // Periodically refresh data every 60 seconds to capture check-in times and updates without page refresh
        const pollInterval = setInterval(() => {
            loadDailyLogs(false);
        }, 60000);

        return () => clearInterval(pollInterval);
    }, [periodDate, isAdmin, currentUserId]);

    // Keep hourCount in sync with maximum hours array length in rows
    useEffect(() => {
        if (rows.length > 0) {
            const maxHours = Math.max(...rows.map(r => r.hours?.length || 0), 12);
            setHourCount(maxHours);
        }
    }, [rows]);

    // ── Hourly Notification Reminder Popup check ──
    useEffect(() => {
        if (isAdmin || rows.length === 0) return;
        const myRow = rows.find(r => r.userId === currentUserId);
        if (!myRow) return;

        const dateKey = `${periodDate}::${myRow.inTime || ''}`;
        if (lastNotifiedDateRef.current !== dateKey) {
            lastNotifiedDateRef.current = dateKey;
            lastNotifiedHourRef.current = null;
        }

        const sendDesktopNotification = (hourIdx) => {
            if ('Notification' in window && Notification.permission === 'granted') {
                try {
                    const n = new Notification('Hourly Production Reminder', {
                        body: `It's time to log your production for the ${ordinal(hourIdx + 1)} hour! You have a 10-minute active window to enter process and count.`,
                        tag: `hourly-reminder-${hourIdx}`,
                        requireInteraction: true
                    });
                    n.onclick = () => {
                        window.focus();
                    };
                } catch (e) {
                    console.warn('Desktop notification failed:', e);
                }
            }
        };

        const checkReminder = () => {
            for (let i = 0; i < hourCount; i++) {
                if (isHourActive(i, myRow.shift, myRow.inTime)) {
                    const hData = myRow.hours?.[i];
                    const hValue = typeof hData === "object" && hData !== null ? (hData.value || "") : (hData || "");
                    const hProc = typeof hData === "object" && hData !== null ? (hData.process || "") : "";

                    if (!hValue || !hProc) {
                        // Only trigger popup once per new hour (not every 30 s within the same window)
                        if (lastNotifiedHourRef.current !== i) {
                            lastNotifiedHourRef.current = i;
                            setNotification({
                                hourIdx: i,
                                message: `🔔 It's time to log your production for the ${ordinal(i + 1)} hour! You have a 10-minute active window to enter process and count.`
                            });
                            playBeep();
                            sendDesktopNotification(i);
                        }
                        return;
                    }
                }
            }
            setNotification(null);
        };

        checkReminder();
        const timer = setInterval(checkReminder, 30000); // Check every 30 seconds
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, hourCount, shifts, periodDate, currentUserId]);

    const getInitials = (name) => {
        if (!name) return "?";
        const parts = name.split(" ").filter(Boolean);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name[0].toUpperCase();
    };

    const getAvatarBg = (name) => {
        if (!name) return "#cbd5e1";
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        const h = Math.abs(hash % 360);
        return `hsl(${h}, 65%, 40%)`;
    };

    const targetUserRow = useMemo(() => {
        let targetUserId = currentUserId;
        if (isPrivileged && selectedUserId) {
            targetUserId = selectedUserId;
        }
        return rows.find(r => r.userId?.toString().toLowerCase() === targetUserId?.toString().toLowerCase());
    }, [rows, currentUserId, isPrivileged, selectedUserId]);

    const userSummaries = useMemo(() => {
        let baseRows = rows.filter(r => !r.excluded);
        let targetUserId = currentUserId;
        if (isPrivileged && selectedUserId) {
            targetUserId = selectedUserId;
        }
        baseRows = baseRows.filter(r => r.userId?.toString().toLowerCase() === targetUserId?.toString().toLowerCase());
        return baseRows
            .map(r => {
                const processesMap = {};
                let hasWork = false;

                if (r.hours && Array.isArray(r.hours)) {
                    r.hours.forEach(h => {
                        const value = parseFloat(h?.value) || 0;
                        if (value > 0) {
                            hasWork = true;
                            const processName = h?.process || r.process || "Unassigned";
                            processesMap[processName] = (processesMap[processName] || 0) + value;
                        }
                    });
                }

                return {
                    userId: r.userId,
                    name: r.name || "Unknown Employee",
                    processes: processesMap,
                    hasWork
                };
            })
            .filter(s => s.hasWork);
    }, [rows, currentUserId, isPrivileged, selectedUserId]);

    // ── Hourly edit timing helper (employees can only edit during active window) ──
    const isHourActive = (hourIdx, shiftName, checkInTimeStr) => {
        if (isAdmin) return true;
        if (periodDate !== getLocalDate()) return false; // compare against local date, not UTC
        if (!checkInTimeStr) return false;

        const parts = checkInTimeStr.split(":").map(Number);
        const sh = parts[0] || 0;
        const sm = parts[1] || 0;

        // Force calculations to Asia/Kolkata (IST) timezone to match server-side check-in timezone
        let nowMins;
        try {
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: 'Asia/Kolkata',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
            const p = formatter.formatToParts(new Date());
            const hour = parseInt(p.find(x => x.type === 'hour').value, 10);
            const minute = parseInt(p.find(x => x.type === 'minute').value, 10);
            nowMins = hour * 60 + minute;
        } catch (e) {
            const d = new Date();
            nowMins = d.getHours() * 60 + d.getMinutes();
        }

        // Window starts exactly (hourIdx + 1) hours after check-in, preserving the minutes.
        // e.g. check-in 8:50 → 1st hour window: 9:50–10:00, 2nd: 10:50–11:00
        // e.g. check-in 8:05 → 1st hour window: 9:05–9:15
        const checkInTotalMins = sh * 60 + sm;
        const windowStartMins = (checkInTotalMins + (hourIdx + 1) * 60) % (24 * 60);
        const windowEndMins = (windowStartMins + 10) % (24 * 60);

        if (windowStartMins <= windowEndMins) {
            // Normal case: window does not cross midnight
            return nowMins >= windowStartMins && nowMins < windowEndMins;
        } else {
            // Window crosses midnight (e.g. 23:55 – 00:05)
            return nowMins >= windowStartMins || nowMins < windowEndMins;
        }
    };

    const canEditHour = (row, hIdx) => {
        if (isEditPrivileged) return true;
        if (row.userId !== currentUserId) return false;
        if (!isTaskStarted) return false;
        return isHourActive(hIdx, row.shift, row.inTime);
    };

    // ── Auto-Save: fires 3 seconds after last change when dirty ──
    useEffect(() => {
        if (changeCount === 0) return;
        const timer = setTimeout(async () => {
            if (!dirtyRef.current) return;
            try {
                setAutoSaving(true);
                const currentRows = rowsRef.current;
                const currentDate = periodDateRef.current;
                const filteredToSave = isEditPrivileged
                    ? currentRows
                    : currentRows.filter(r => r.userId === currentUserId);
                if (filteredToSave.length > 0) {
                    await apiCall("/hourly-graph/logs", "POST", {
                        date: currentDate,
                        rows: filteredToSave
                    });
                }
                setSavedAt(new Date());
                setDirty(false);
                setError("");
            } catch (err) {
                // Show visible error so user knows to save manually
                setError(`Auto-save failed: ${err.message || "Please click Save Report to save your data."}`);
                console.warn("Auto-save failed:", err.message);
            } finally {
                setAutoSaving(false);
            }
        }, 3000);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [changeCount, isEditPrivileged, currentUserId]);

    // ── Save Function ──
    const handleSave = async () => {
        try {
            setError("");
            if (isAdmin) {
                // Save target settings
                await apiCall("/hourly-graph/settings", "POST", {
                    columnGroups,
                    targetRows
                });
            }

            // Save daily logs
            const filteredToSave = isEditPrivileged ? rows : rows.filter(r => r.userId === currentUserId);
            if (filteredToSave.length > 0) {
                await apiCall("/hourly-graph/logs", "POST", {
                    date: periodDate,
                    rows: filteredToSave
                });
            }

            setSavedAt(new Date());
            setDirty(false);
            alert("Report saved successfully!");
        } catch (err) {
            console.error("Save failed:", err);
            setError(err.message || "Failed to save Hourly Graph report.");
            alert("Save failed: " + (err.message || "Unknown error"));
        }
    };

    // ── Save Targets specifically Function ──
    const handleSaveTargets = async () => {
        try {
            setError("");
            await apiCall("/hourly-graph/settings", "POST", {
                columnGroups,
                targetRows
            });
            setSavedAt(new Date());
            setDirty(false);
            alert("Daily production targets saved successfully!");
        } catch (err) {
            console.error("Failed to save targets:", err);
            setError(err.message || "Failed to save daily production targets.");
            alert("Save failed: " + (err.message || "Unknown error"));
        }
    };

    // ── Visibility / Exclude Icon handler ──
    const handleToggleVisibility = async (userId, currentExcluded) => {
        const action = currentExcluded ? "show" : "hide";
        if (!window.confirm(`Are you sure you want to ${action} this employee?`)) return;
        try {
            const nextExcluded = !currentExcluded;
            await apiCall(`/hourly-graph/users/${userId}/toggle-visibility`, "POST", { exclude: nextExcluded });
            setRows(prev => prev.map(r => r.userId === userId ? { ...r, excluded: nextExcluded } : r));
        } catch (err) {
            console.error("Failed to toggle visibility:", err);
            alert("Error: " + err.message);
        }
    };

    // ── Row updates ──
    const updateRow = (id, field, value) => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
        markDirty();
    };

    const updateHour = (id, hourIdx, field, value) => {
        setRows((prev) =>
            prev.map((r) => {
                if (r.id !== id) return r;
                const hours = [...(r.hours || [])];
                while (hours.length <= hourIdx) {
                    hours.push({ process: "", value: "" });
                }
                const oldItem = hours[hourIdx];
                const oldObj = typeof oldItem === "object" && oldItem !== null ? oldItem : { process: "", value: oldItem || "" };
                hours[hourIdx] = { ...oldObj, [field]: value };
                return { ...r, hours };
            })
        );
        markDirty();
    };


    const addHourColumn = () => {
        if (!isAdmin) return;
        setHourCount((c) => c + 1);
        setRows((prev) => prev.map((r) => ({ ...r, hours: [...r.hours, { process: "", value: "" }] })));
        markDirty();
    };

    // ── Target value handlers (Admin only) ──
    const updateTargetProject = (id, value) => {
        if (!isAdmin) return;
        setTargetRows((prev) => prev.map((r) => (r.id === id ? { ...r, project: value } : r)));
        markDirty();
    };

    const updateTargetValue = (id, groupKey, colId, value) => {
        if (!isAdmin) return;
        setTargetRows((prev) =>
            prev.map((r) => (r.id === id ? { ...r, values: { ...r.values, [vk(groupKey, colId)]: value } } : r))
        );
        markDirty();
    };

    const addTargetRow = () => {
        if (!isAdmin) return;
        const newId = `tgt-${Date.now()}`;
        setTargetRows((prev) => [...prev, { id: newId, project: "", values: {}, isNew: true }]);
        markDirty();
    };

    const removeTargetRow = (id) => {
        if (!isAdmin) return;
        if (!window.confirm("Are you sure you want to delete this project target row?")) return;
        setTargetRows((prev) => prev.filter((r) => r.id !== id));
        markDirty();
    };

    // ── Drag-to-reorder handlers for target rows ──
    const handleDragStart = (e, id) => {
        setDragRowId(id);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e, id) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (id !== dragRowId) setDragOverRowId(id);
    };

    const handleDrop = async (e, targetId) => {
        e.preventDefault();
        if (!dragRowId || dragRowId === targetId) {
            setDragRowId(null);
            setDragOverRowId(null);
            return;
        }
        // Compute new order synchronously so we can save it immediately
        let reordered;
        setTargetRows((prev) => {
            const next = [...prev];
            const fromIdx = next.findIndex((r) => r.id === dragRowId);
            const toIdx = next.findIndex((r) => r.id === targetId);
            if (fromIdx === -1 || toIdx === -1) { reordered = prev; return prev; }
            const [moved] = next.splice(fromIdx, 1);
            next.splice(toIdx, 0, moved);
            reordered = next;
            return next;
        });
        setDragRowId(null);
        setDragOverRowId(null);
        // Auto-save the new order immediately (don't wait for user to click Save)
        try {
            await apiCall("/hourly-graph/settings", "POST", {
                columnGroups,
                targetRows: reordered,
            });
            setSavedAt(new Date());
            setDirty(false);
        } catch (err) {
            console.error("Failed to auto-save row order:", err);
            markDirty(); // Fall back to manual save
        }
    };

    const handleDragEnd = () => {
        setDragRowId(null);
        setDragOverRowId(null);
    };

    // ── Column-group header handlers (Admin only) ──
    const updateGroupLabel = (groupKey, label) => {
        if (!isAdmin) return;
        setColumnGroups((prev) => prev.map((g) => (g.key === groupKey ? { ...g, label } : g)));
        markDirty();
    };

    const updateColumnLabel = (groupKey, colId, label) => {
        if (!isAdmin) return;
        setColumnGroups((prev) =>
            prev.map((g) =>
                g.key === groupKey ? { ...g, columns: g.columns.map((c) => (c.id === colId ? { ...c, label } : c)) } : g
            )
        );
        markDirty();
    };

    const removeGroup = (groupKey) => {
        if (!isAdmin) return;
        if (!window.confirm("Are you sure you want to delete this column group and all its columns?")) return;
        setColumnGroups((prev) => (prev.length > 1 ? prev.filter((g) => g.key !== groupKey) : prev));
        markDirty();
    };

    const removeColumn = (groupKey, colId) => {
        if (!isAdmin) return;
        if (!window.confirm("Are you sure you want to delete this column?")) return;
        setColumnGroups((prev) => {
            const next = prev
                .map((g) => (g.key === groupKey ? { ...g, columns: g.columns.filter((c) => c.id !== colId) } : g))
                .filter((g) => g.columns.length > 0);
            return next.length > 0 ? next : prev;
        });
        markDirty();
    };

    const startAddColumn = (groupKey) => {
        if (!isAdmin) return;
        setAddingGroup(groupKey);
        setNewColName("");
    };

    const cancelAddColumn = () => {
        setAddingGroup(null);
        setNewColName("");
    };

    const confirmAddColumn = () => {
        if (!isAdmin) return;
        const name = newColName.trim();
        if (!name) {
            cancelAddColumn();
            return;
        }
        if (addingGroup === "__new__") {
            const newKey = `grp-${Date.now()}`;
            setColumnGroups((prev) => [
                ...prev,
                {
                    key: newKey,
                    label: name,
                    tint: ROW_BANDS[prev.length % ROW_BANDS.length],
                    columns: [{ id: `col-${Date.now()}`, label: "Value" }],
                },
            ]);
        } else {
            setColumnGroups((prev) =>
                prev.map((g) => (g.key === addingGroup ? { ...g, columns: [...g.columns, { id: `col-${Date.now()}`, label: name }] } : g))
            );
        }
        cancelAddColumn();
        markDirty();
    };

    const filteredRows = useMemo(() => {
        return rows.filter((r) => (search ? r.name.toLowerCase().includes(search.toLowerCase()) : true));
    }, [rows, search]);
    const totalTargetCols = 2 + columnGroups.reduce((a, g) => a + g.columns.length, 0) + (isAdmin ? 1 : 0);
    const totalEmployeeCols = 6 + hourLabels.length * 2 + (isAdmin ? 1 : 0);

    if (!isAdmin && !isCheckedIn) {
        return (
            <div className="emp-locked-container" style={{ padding: "80px 20px", display: "flex", justifyContent: "center" }}>
                <div className="emp-locked-card" style={{ background: "rgba(255, 255, 255, 0.75)", backdropFilter: "blur(16px)", border: "1px solid var(--hg-line)", borderRadius: "var(--hg-radius-lg)", padding: "40px", maxWidth: "450px", textAlign: "center", boxShadow: "var(--hg-shadow-md)" }}>
                    <div className="emp-locked-icon" style={{ fontSize: "50px", marginBottom: "16px" }}>🔒</div>
                    <h3 style={{ margin: "0 0 10px 0", fontWeight: 700 }}>Hourly Graph Locked</h3>
                    <p style={{ color: "var(--hg-text-sub)", fontSize: "14px", lineHeight: "1.6", margin: "0 0 20px 0" }}>You must Check-In to your shift to access the Hourly Graph page and record your production updates.</p>
                </div>
            </div>
        );
    }

    if (loading && rows.length === 0) {
        return <div className="hg-wrapper" style={{ padding: "40px", textAlign: "center" }}><h3>Loading Hourly Production Grid...</h3></div>;
    }

    return (
        <div className="hg-wrapper">
            {error && <div className="hg-error-banner">⚠️ {error}</div>}
            {!isTaskStarted && !isAdmin && !currentUser?.roles?.includes("Manager") && (
                <div className="hg-warning-banner" style={{
                    background: "rgba(255, 59, 92, 0.08)",
                    border: "1px solid rgba(255, 59, 92, 0.18)",
                    borderRadius: "var(--hg-radius-md)",
                    padding: "12px 16px",
                    color: "var(--pink)",
                    fontSize: "0.85rem",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    boxShadow: "var(--hg-shadow-sm)",
                    marginTop: "8px",
                    marginBottom: "8px"
                }}>
                    <span>⚠️</span>
                    <span>You must start a task first in Workwise to record hourly production updates.</span>
                </div>
            )}

            {/* ── Page Header ── */}
            <div className="hg-page-header">
                <div className="hg-page-title">
                    <span className="hg-page-icon">📊</span>
                    <h2>Hourly Production Report</h2>
                </div>
            </div>

            {/* ── Daily Production Target Graph ── */}
            {showTargetGraph && (
                <div className="hg-target-head">
                    <div className="hg-target-banner">
                        Arrow Data-Tech Daily Production Target Graph - {monthYearLabel}
                    </div>
                </div>
            )}

            <div className="hg-target-section-layout">
                {showTargetGraph && (
                    <div className="hg-sheet-card hg-target-card">
                        <div className="hg-table-container">
                            <table className="hg-table hg-target-table">
                                <thead>
                                    <tr>
                                        <th rowSpan={2} className="th-fixed th-sno">S.No</th>
                                        <th rowSpan={2} className="th-fixed th-project">Project Name</th>
                                        {columnGroups.map((group) => (
                                            <th key={group.key} colSpan={group.columns.length} className={`th-group th-group-${group.tint}`}>
                                                <div className="hg-group-header">
                                                    <EditableHeader
                                                        value={group.label}
                                                        onChange={(v) => updateGroupLabel(group.key, v)}
                                                        className="hg-group-label"
                                                        placeholder="Group name"
                                                        disabled={!isAdmin}
                                                    />
                                                    {isAdmin && columnGroups.length > 1 && (
                                                        <button
                                                            className="hg-del-col-btn"
                                                            onClick={() => removeGroup(group.key)}
                                                            title={`Remove ${group.label} group`}
                                                        >
                                                            ✕
                                                        </button>
                                                    )}
                                                    {isAdmin && (addingGroup === group.key ? (
                                                        <span className="hg-add-col-inline" onClick={(e) => e.stopPropagation()}>
                                                            <input
                                                                autoFocus
                                                                className="hg-add-col-input"
                                                                placeholder="Column name"
                                                                value={newColName}
                                                                onChange={(e) => setNewColName(e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter") confirmAddColumn();
                                                                    if (e.key === "Escape") cancelAddColumn();
                                                                }}
                                                            />
                                                            <button className="hg-add-col-ok" onClick={confirmAddColumn} title="Add">✓</button>
                                                            <button className="hg-add-col-cancel" onClick={cancelAddColumn} title="Cancel">✕</button>
                                                        </span>
                                                    ) : (
                                                        <button className="hg-add-col-btn" onClick={() => startAddColumn(group.key)} title={`Add column to ${group.label}`}>
                                                            +
                                                        </button>
                                                    ))}
                                                </div>
                                            </th>
                                        ))}
                                        {isAdmin && (
                                            <th rowSpan={2} className="th-add-group">
                                                {addingGroup === "__new__" ? (
                                                    <span className="hg-add-col-inline" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            autoFocus
                                                            className="hg-add-col-input"
                                                            placeholder="Group name"
                                                            value={newColName}
                                                            onChange={(e) => setNewColName(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter") confirmAddColumn();
                                                                if (e.key === "Escape") cancelAddColumn();
                                                            }}
                                                        />
                                                        <button className="hg-add-col-ok" onClick={confirmAddColumn} title="Add group">✓</button>
                                                        <button className="hg-add-col-cancel" onClick={cancelAddColumn} title="Cancel">✕</button>
                                                    </span>
                                                ) : (
                                                    <button className="hg-add-group-btn" onClick={() => startAddColumn("__new__")} title="Add a new column group">
                                                        + Group
                                                    </button>
                                                )}
                                            </th>
                                        )}
                                    </tr>
                                    <tr>
                                        {columnGroups.map((group) =>
                                            group.columns.map((c) => (
                                                <th key={`${group.key}-${c.id}`} className={`th-group th-group-${group.tint}`}>
                                                    <div className="hg-col-header">
                                                        <EditableHeader
                                                            value={c.label}
                                                            onChange={(v) => updateColumnLabel(group.key, c.id, v)}
                                                            placeholder="Column name"
                                                            disabled={!isAdmin}
                                                        />
                                                        {isAdmin && (
                                                            <button
                                                                className="hg-del-col-btn hg-del-col-btn--sm"
                                                                onClick={() => removeColumn(group.key, c.id)}
                                                                title={`Remove ${c.label} column`}
                                                            >
                                                                ✕
                                                            </button>
                                                        )}
                                                    </div>
                                                </th>
                                            ))
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {targetRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={totalTargetCols} className="hg-empty">No project targets configured.</td>
                                        </tr>
                                    ) : (
                                        targetRows.map((row, idx) => (
                                            <tr
                                                key={row.id}
                                                className={`hg-row ${ROW_BANDS[idx % ROW_BANDS.length]}${dragOverRowId === row.id ? ' hg-row-drag-over' : ''}`}
                                                draggable={isAdmin}
                                                onDragStart={isAdmin ? (e) => handleDragStart(e, row.id) : undefined}
                                                onDragOver={isAdmin ? (e) => handleDragOver(e, row.id) : undefined}
                                                onDrop={isAdmin ? (e) => handleDrop(e, row.id) : undefined}
                                                onDragEnd={isAdmin ? handleDragEnd : undefined}
                                                style={dragRowId === row.id ? { opacity: 0.45 } : {}}
                                            >
                                                <td className="td-sno">
                                                    {isAdmin && (
                                                        <span className="hg-drag-handle" title="Drag to reorder">≡</span>
                                                    )}
                                                    <span className="hg-sno-num">{idx + 1}</span>
                                                    {isAdmin && (
                                                        <button className="hg-sno-remove" onClick={() => removeTargetRow(row.id)} title="Remove row">✕</button>
                                                    )}
                                                </td>
                                                <td className="td-project">
                                                    {isAdmin ? (
                                                        <select
                                                            className="hg-cell-select tgt-project-select"
                                                            value={row.project}
                                                            onChange={(e) => updateTargetProject(row.id, e.target.value)}
                                                        >
                                                            <option value="">Select project…</option>
                                                            {projects.map((p) => {
                                                                const formatted = p.clientName ? `${p.clientName}_${p.name}` : p.name;
                                                                return <option key={p.id} value={formatted}>{formatted}</option>;
                                                            })}
                                                        </select>
                                                    ) : (
                                                        <span style={{ fontWeight: 700, paddingLeft: "5px" }}>{row.project}</span>
                                                    )}
                                                </td>
                                                {columnGroups.map((group) =>
                                                    group.columns.map((c) => (
                                                        <td key={`${group.key}-${c.id}`}>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                className="hg-cell-input hg-cell-input--value"
                                                                placeholder="-"
                                                                value={row.values[vk(group.key, c.id)] ?? ""}
                                                                onChange={(e) => updateTargetValue(row.id, group.key, c.id, e.target.value)}
                                                                disabled={!isAdmin}
                                                            />
                                                        </td>
                                                    ))
                                                )}
                                                {isAdmin && <td className="td-add-col-spacer" />}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                                {isAdmin && (
                                    <tfoot>
                                        <tr>
                                            <td colSpan={totalTargetCols} style={{ padding: "6px 8px" }}>
                                                <div style={{ display: "flex", gap: "10px" }}>
                                                    <button className="hg-add-btn" onClick={addTargetRow}>+ Add Project Row</button>
                                                    <button className="hg-save-btn" onClick={handleSaveTargets}>💾</button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>
                )}

                {/* ── Daily Productivity Summaries by User ── */}
                <div className="hg-summary-container" style={!showTargetGraph ? { width: "100%", maxWidth: "100%" } : {}}>
                    <div className="hg-summary-header">
                        <span className="hg-summary-icon">✨</span>
                        <h3>Daily Production Card</h3>
                    </div>
                    <div className="hg-summary-scroll">
                        {userSummaries.length === 0 ? (
                            <div className="hg-summary-empty">No work recorded today for {targetUserRow?.name || "this user"}.</div>
                        ) : (
                            userSummaries.map((summary) => (
                                <div key={summary.userId || summary.name} className="hg-summary-card">
                                    <div className="hg-summary-user">
                                        <div
                                            className="hg-summary-avatar"
                                            style={{ backgroundColor: getAvatarBg(summary.name) }}
                                        >
                                            {getInitials(summary.name)}
                                        </div>
                                        <div className="hg-summary-user-info">
                                            <div className="hg-summary-user-name">{summary.name}</div>
                                            <div className="hg-summary-user-subtitle">Active today</div>
                                        </div>
                                    </div>
                                    <div className="hg-summary-detail-list">
                                        {Object.entries(summary.processes).map(([processName, pages]) => (
                                            <div key={processName} className="hg-summary-detail-row">
                                                <div className="hg-summary-process-name">
                                                    <span className="hg-summary-bullet" />
                                                    {processName}
                                                </div>
                                                <div className="hg-summary-pages-badge">
                                                    <strong>{pages}</strong>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* ── Slim functional toolbar ── */}
            <div className="hg-toolbar">
                <div className="hg-search-wrap">
                    <span className="hg-search-icon">🔍</span>
                    <input
                        className="hg-search"
                        placeholder="Search employee…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="hg-toolbar-actions">
                    {autoSaving ? (
                        <span className="hg-unsaved-tag">⏳ Auto-saving...</span>
                    ) : dirty ? (
                        <span className="hg-unsaved-tag">● Unsaved changes</span>
                    ) : savedAt ? (
                        <span className="hg-saved-tag">✓ Auto-saved {savedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>
                    ) : null}
                    <button 
                        className="hg-save-btn" 
                        onClick={handleSave}
                        disabled={!isEditPrivileged && !isTaskStarted}
                        style={(!isEditPrivileged && !isTaskStarted) ? { opacity: 0.5, cursor: "not-allowed", background: "gray", boxShadow: "none" } : {}}
                    >
                        💾 Save Report
                    </button>
                </div>
            </div>

            {/* ── Sheet header: DATE / DAY block + title banner ── */}
            <div className="hg-sheet-head">
                <div className="hg-dd-widget">
                    <div className="hg-dd-col">
                        <div className="hg-dd-label">DATE</div>
                        <div className="hg-dd-value" onClick={() => dateInputRef.current?.showPicker?.() || dateInputRef.current?.focus()}>
                            {parseLocalDate(periodDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                            <input
                                ref={dateInputRef}
                                type="date"
                                className="hg-date-hidden"
                                value={periodDate}
                                onChange={(e) => handleDateChange(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="hg-dd-col">
                        <div className="hg-dd-label">DAY</div>
                        <span className="hg-dd-day-label">{activeDay || "—"}</span>
                    </div>
                </div>

                <div className="hg-title-banner">
                    Arrow Data-Tech Employee Daily Hourly Production Report Details - {monthYearLabel}
                </div>
            </div>

            {/* ── Sheet table ── */}
            <div className="hg-sheet-card">
                <div className="hg-table-container">
                    <table className="hg-table">
                        <thead>
                            <tr>
                                <th className="th-sno">S.NO</th>
                                <th className="th-name">NAME</th>
                                <th className="th-shift">SHIFT DETAILS</th>
                                <th className="th-time">IN TIME</th>
                                <th className="th-time">OUT TIME</th>
                                <th className="th-project">PROJECT NAME</th>
                                {hourLabels.map((h, i) => (
                                    <React.Fragment key={i}>
                                        <th className={`th-${hourAccent(i)} th-hour-process`}>PROCESS</th>
                                        <th className={`th-${hourAccent(i)}`}>{h} HOUR</th>
                                    </React.Fragment>
                                ))}
                                {isAdmin && (
                                    <th className="th-add-col">
                                        <button className="hg-add-hour-btn" onClick={addHourColumn} title="Add a process/hour column">+</button>
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRows.length === 0 ? (
                                <tr>
                                    <td colSpan={totalEmployeeCols} className="hg-empty">
                                        No employees match your search.
                                    </td>
                                </tr>
                            ) : (
                                filteredRows.map((row, idx) => {
                                    const isSelf = row.userId === currentUserId;
                                    const canEditRow = isEditPrivileged || (isSelf && isTaskStarted);
                                    const isAbsent = row.shift === "Absent";

                                    return (
                                        <tr
                                            key={row.id}
                                            className={`hg-row ${ROW_BANDS[idx % ROW_BANDS.length]} ${selectedUserId === row.userId ? "hg-row--selected" : ""} ${isAbsent ? "hg-row--absent" : ""}`}
                                            style={row.excluded ? { opacity: 0.55, border: "2px dashed rgba(255, 255, 255, 0.4)" } : {}}
                                        >
                                            <td className="td-sno">
                                                <span className="hg-sno-num">{idx + 1}</span>
                                            </td>

                                            <td className="td-name" style={{ cursor: isPrivileged ? "pointer" : "default" }} onClick={() => {
                                                if (isPrivileged) {
                                                    setSelectedUserId(row.userId === selectedUserId ? null : row.userId);
                                                }
                                            }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                    {isAdmin && row.userId && (
                                                        <button
                                                            className="hg-exclude-btn"
                                                            onClick={() => handleToggleVisibility(row.userId, row.excluded)}
                                                            title={row.excluded ? "Show this employee to other users" : "Hide this employee from other users"}
                                                            style={{ background: "none", border: "none", cursor: "pointer", fontSize: "14px" }}
                                                        >
                                                            {row.excluded ? "🙈" : "👁️"}
                                                        </button>
                                                    )}
                                                    <span className="hg-cell-name-label">{row.name}</span>
                                                    {row.excluded && <span style={{ fontSize: "10px", opacity: 0.7, marginLeft: "4px" }}>(Hidden)</span>}
                                                </div>
                                            </td>

                                            <td className="td-shift">
                                                <select
                                                    className="hg-cell-select"
                                                    value={row.shift}
                                                    onChange={(e) => updateRow(row.id, "shift", e.target.value)}
                                                    disabled={!isAdmin}
                                                >
                                                    <option value="Absent">Absent</option>
                                                    {!shifts.some((s) => s.name?.toLowerCase() === 'trainee') && (
                                                        <option value="Trainee">Trainee</option>
                                                    )}
                                                    {shifts.map((s) => (
                                                        <option key={s.id} value={s.name}>{s.name}</option>
                                                    ))}
                                                </select>
                                            </td>

                                            <td className="td-time">
                                                <input
                                                    type="time"
                                                    className="hg-cell-input td-checkin"
                                                    value={row.inTime}
                                                    disabled
                                                />
                                            </td>

                                            <td className="td-time">
                                                <input
                                                    type="time"
                                                    className="hg-cell-input td-checkout"
                                                    value={row.outTime || ""}
                                                    onChange={(e) => updateRow(row.id, "outTime", e.target.value)}
                                                    disabled={!isAdmin}
                                                />
                                            </td>

                                            <td className="td-project" style={{ position: "relative" }}>
                                                {(!canEditRow || isAbsent) ? (
                                                    <span className="hg-project-text-disabled" title={row.project || "—"}>
                                                        {row.project || "—"}
                                                    </span>
                                                ) : (
                                                    (() => {
                                                        const isDropdownOpen = openProjectDropdownRowId === row.id;
                                                        const selectedProjects = row.project ? row.project.split(",").map(p => p.trim()).filter(Boolean) : [];

                                                        return (
                                                            <div className={`hg-project-multiselect-container ${isDropdownOpen ? "active" : ""}`}>
                                                                <button
                                                                    type="button"
                                                                    className="hg-project-multiselect-trigger hg-cell-select hg-cell-select--project"
                                                                    onClick={() => {
                                                                        setOpenProjectDropdownRowId(isDropdownOpen ? null : row.id);
                                                                    }}
                                                                    title={row.project || "Select projects..."}
                                                                >
                                                                    <span className="hg-trigger-text">
                                                                        {selectedProjects.length > 0
                                                                            ? selectedProjects.join(", ")
                                                                            : "—"}
                                                                    </span>
                                                                    <span className="hg-trigger-arrow">▼</span>
                                                                </button>
                                                                {isDropdownOpen && (
                                                                    (() => {
                                                                        const openUp = filteredRows.length > 1 && idx >= filteredRows.length - 2;
                                                                        return (
                                                                            <div className={`hg-project-multiselect-dropdown ${openUp ? "up" : ""}`} onClick={(e) => e.stopPropagation()}>
                                                                                <div className="hg-dropdown-actions">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="hg-dropdown-action-btn"
                                                                                        onClick={() => updateRow(row.id, "project", "")}
                                                                                    >
                                                                                        Clear All
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="hg-dropdown-action-btn"
                                                                                        onClick={() => {
                                                                                            const allFormatted = projects
                                                                                                .map(p => p.clientName ? `${p.clientName}_${p.name}` : p.name)
                                                                                                .join(", ");
                                                                                            updateRow(row.id, "project", allFormatted);
                                                                                        }}
                                                                                    >
                                                                                        Select All
                                                                                    </button>
                                                                                </div>
                                                                                <div className="hg-dropdown-options">
                                                                                    {projects.map((p) => {
                                                                                        const formatted = p.clientName ? `${p.clientName}_${p.name}` : p.name;
                                                                                        const isChecked = selectedProjects.includes(formatted);
                                                                                        return (
                                                                                            <label key={p.id} className={`hg-dropdown-option ${isChecked ? "checked" : ""}`}>
                                                                                                <input
                                                                                                    type="checkbox"
                                                                                                    checked={isChecked}
                                                                                                    onChange={() => {
                                                                                                        let nextSelected;
                                                                                                        if (isChecked) {
                                                                                                            nextSelected = selectedProjects.filter(sp => sp !== formatted);
                                                                                                        } else {
                                                                                                            nextSelected = [...selectedProjects, formatted];
                                                                                                        }
                                                                                                        updateRow(row.id, "project", nextSelected.join(", "));
                                                                                                    }}
                                                                                                />
                                                                                                <span className="hg-option-text">{formatted}</span>
                                                                                            </label>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })()
                                                                )}
                                                            </div>
                                                        );
                                                    })()
                                                )}
                                            </td>

                                            {Array.from({ length: hourLabels.length }).map((_, hIdx) => {
                                                const hData = row.hours?.[hIdx] || { process: "", value: "" };
                                                const hValue = typeof hData === "object" && hData !== null ? (hData.value || "") : (hData || "");
                                                const hProcess = typeof hData === "object" && hData !== null ? (hData.process || "") : "";
                                                const isEditable = canEditHour(row, hIdx);

                                                return (
                                                    <React.Fragment key={hIdx}>
                                                        <td className={`td-${hourAccent(hIdx)} td-hour-process`}>
                                                            <select
                                                                className="hg-cell-select hg-cell-select--process"
                                                                value={hProcess}
                                                                onChange={(e) => updateHour(row.id, hIdx, "process", e.target.value)}
                                                                disabled={!isEditable || isAbsent}
                                                            >
                                                                <option value="">—</option>
                                                                {processes.map((p) => (
                                                                    <option key={p.id} value={p.name}>{p.name}</option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                        <td className={`td-${hourAccent(hIdx)}`}>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                className="hg-cell-input hg-cell-input--value"
                                                                placeholder=""
                                                                value={hValue}
                                                                onChange={(e) => updateHour(row.id, hIdx, "value", e.target.value)}
                                                                disabled={!isEditable || isAbsent}
                                                            />
                                                        </td>
                                                    </React.Fragment>
                                                );
                                            })}

                                            {isAdmin && <td className="td-add-col-spacer" />}
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Floating Pop-Up Notification Reminder ── */}
            {notification && (
                <div className="hg-popup-notification">
                    <span className="hg-popup-close" onClick={() => setNotification(null)}>✕</span>
                    <p>{notification.message}</p>
                </div>
            )}

            {/* ── Dropdown Overlay ── */}
            {openProjectDropdownRowId && (
                <div className="hg-dropdown-overlay" onClick={() => setOpenProjectDropdownRowId(null)} />
            )}
        </div>
    );
}